import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import LSWLogo from "@/components/LSWLogo";
import { ArrowRight, Loader2 } from "lucide-react";

function formatErr(detail) {
  if (!detail) return "Si è verificato un errore. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function AuthScreen() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [form, setForm] = useState({ nome: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const isRegister = mode === "register";
  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (isRegister && !form.nome.trim()) {
      setError("Inserisci il tuo nome");
      return;
    }
    setLoading(true);
    try {
      if (isRegister) {
        await register(form.nome.trim(), form.email.trim().toLowerCase(), form.password);
      } else {
        await login(form.email.trim().toLowerCase(), form.password);
      }
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full flex justify-center"
      data-testid="auth-screen"
      style={{ minHeight: "100dvh" }}
    >
      <div
        className="relative w-full max-w-md min-h-screen bg-[#f9f5ef] shadow-[0_30px_80px_-30px_rgba(46,40,32,0.35)] overflow-y-auto overflow-x-hidden"
        style={{ minHeight: "100dvh" }}
      >
        <div className="lsw-hero-gradient px-6 pt-16 pb-10 rounded-b-[32px]">
          <div className="flex justify-center">
            <LSWLogo size={70} layout="vertical" />
          </div>
        </div>

        <div className="px-6 pt-10 pb-10 lsw-fade-up">
          <h1
            data-testid="auth-title"
            className="font-serif-display font-light text-[42px] leading-[1.05] tracking-tight text-[#2e2820]"
          >
            {isRegister ? (
              <>Crea il tuo<br/><span className="italic text-[#1535a0] font-normal">cammino</span></>
            ) : (
              <>Benvenuto<br/><span className="italic text-[#1535a0] font-normal">nel tuo spazio</span></>
            )}
          </h1>
          <p className="font-body font-light text-base text-[#7a6f65] leading-relaxed mt-4">
            {isRegister
              ? "Registrati per accedere a pratiche, viaggi e contenuti esclusivi."
              : "Accedi per continuare il tuo percorso di trasformazione."}
          </p>

          <form onSubmit={handleSubmit} className="mt-7 flex flex-col gap-3" data-testid="auth-form">
            {isRegister && (
              <div>
                <label className="font-body text-xs tracking-[0.2em] uppercase text-[#7a6f65] ml-1">Nome</label>
                <input
                  data-testid="auth-input-nome"
                  type="text"
                  value={form.nome}
                  onChange={onChange("nome")}
                  placeholder="Il tuo nome"
                  autoComplete="name"
                  className="mt-1.5 w-full bg-[#f9f5ef] border border-[#ede7df] rounded-[18px] px-4 py-4 text-[#2e2820] font-body placeholder:text-[#7a6f65]/60 focus:outline-none focus:ring-2 focus:ring-[#1535a0] focus:border-transparent transition-all"
                  required
                />
              </div>
            )}
            <div>
              <label className="font-body text-xs tracking-[0.2em] uppercase text-[#7a6f65] ml-1">Email</label>
              <input
                data-testid="auth-input-email"
                type="email"
                value={form.email}
                onChange={onChange("email")}
                placeholder="tu@esempio.it"
                autoComplete="email"
                className="mt-1.5 w-full bg-[#f9f5ef] border border-[#ede7df] rounded-[18px] px-4 py-4 text-[#2e2820] font-body placeholder:text-[#7a6f65]/60 focus:outline-none focus:ring-2 focus:ring-[#1535a0] focus:border-transparent transition-all"
                required
              />
            </div>
            <div>
              <label className="font-body text-xs tracking-[0.2em] uppercase text-[#7a6f65] ml-1">Password</label>
              <input
                data-testid="auth-input-password"
                type="password"
                value={form.password}
                onChange={onChange("password")}
                placeholder="Almeno 6 caratteri"
                minLength={6}
                autoComplete={isRegister ? "new-password" : "current-password"}
                className="mt-1.5 w-full bg-[#f9f5ef] border border-[#ede7df] rounded-[18px] px-4 py-4 text-[#2e2820] font-body placeholder:text-[#7a6f65]/60 focus:outline-none focus:ring-2 focus:ring-[#1535a0] focus:border-transparent transition-all"
                required
              />
            </div>

            {error && (
              <div
                data-testid="auth-error"
                className="font-body text-sm text-[#941a7c] bg-[#941a7c]/10 rounded-[14px] px-4 py-3 border border-[#941a7c]/20"
              >
                {error}
              </div>
            )}

            <button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="mt-3 w-full bg-[#1535a0] text-[#f9f5ef] rounded-full py-4 font-body font-medium text-base tracking-wide transition-all hover:bg-[#0f2880] active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" strokeWidth={1.5} />
              ) : (
                <>
                  {isRegister ? "Registrati" : "Accedi"}
                  <ArrowRight strokeWidth={1.5} className="w-5 h-5" />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center pb-20">
            <button
              data-testid="auth-toggle-mode"
              type="button"
              onClick={() => { setMode(isRegister ? "login" : "register"); setError(""); }}
              className="font-body text-sm text-[#7a6f65] hover:text-[#1535a0] transition-colors"
            >
              {isRegister ? (
                <>Hai già un account? <span className="font-medium text-[#1535a0] underline-offset-2 hover:underline">Accedi</span></>
              ) : (
                <>Non hai un account? <span className="font-medium text-[#1535a0] underline-offset-2 hover:underline">Registrati</span></>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
